import { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Box, Stack } from '@mui/material';
import { useStyles } from '../styles/sectionStyles';
import SectionHeader from './SectionHeader';
import TaskCard from './TaskCard';
import AddTaskForm from '../../projects/boardview/components/AddTaskForm';
import { getSectionsByProjectId } from '../../../services/sectionService';
import { getProjectById } from '../../../services/projectService';
import { isAdmin, isStakeholder } from '../../../utils/share';
import { Loader } from '../../../components/loader';
import { PROJECT_EDITOR_ROLE } from '../../../Constants/ProjectConstants';

const Section = ({ section, setLoading, runningTask, setRunningTask, handleTaskClick, allTask, onRefetch }) => {
  const classes = useStyles({ isOver: false });
  const [taskList, setTaskList] = useState(allTask);
  const [taskCount, setTaskCount] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSectionLoading, setIsSectionLoading] = useState(false);
  const [firstSectionId, setFirstSectionId] = useState(1); // Default fallback
  const [issuesSectionId, setIssuesSectionId] = useState(null);
  const [projectDetails, setProjectDetails] = useState(null);
  const sectionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const accountData = useSelector((state) => state?.login?.user);

  const userIsAdmin = isAdmin();
  const userIsStakeholder = isStakeholder();

  // Fetch sections for the project to get the first section ID
  useEffect(() => {
    const fetchSections = async () => {
      if (section?.id) {
        try {
          const response = await getSectionsByProjectId(section.id);
          const sections = response?.data?.data?.rows || response?.data?.data || [];
          if (sections.length > 0) {
            setFirstSectionId(sections[0].id);
            const issues = sections.find((s) => (s?.name || '').toLowerCase().includes('issues'));
            if (issues?.id) setIssuesSectionId(issues.id);
          }
        } catch (error) {
          console.error('Error fetching sections:', error);
        }
      }
    };
    fetchSections();
  }, [section?.id]);

  // Fetch project details if user is admin or stakeholder (to get project members for assignee dropdown)
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (section?.id) {
        try {
          const response = await getProjectById(section.id);
          setProjectDetails(response?.data?.data);
        } catch (error) {
          console.error('Error fetching project details:', error);
        }
      }
    };

    fetchProjectDetails();
  }, [userIsAdmin, userIsStakeholder, section?.id]);

  useEffect(() => {
    const projectTasks = allTask?.filter((item) => item?.projectId === section?.id) || [];
    setTaskCount(projectTasks.length);
    setTaskList(projectTasks); // Update taskList when allTask changes
    // Turn off section loading when data is updated
    if (isSectionLoading) {
      setIsSectionLoading(false);
    }
  }, [allTask, section?.id, isSectionLoading]);

  const handleToggleAddForm = useCallback(() => {
    setShowAddForm((prev) => !prev);
  }, [section?.id]);

  const handleAddFormCancel = () => {
    setShowAddForm(false);
  };

  const handleAddFormSubmit = () => {
    setShowAddForm(false);
    setIsSectionLoading(true);
    // Trigger a refetch to update the task list after task creation (without page loader)
    if (onRefetch) {
      onRefetch(false);
    }
  };

  const roleName = projectDetails?.projectmember?.find((item) => item?.userId === accountData?.id)?.projectRoles
    ?.roleName;
  const isEditPermission = PROJECT_EDITOR_ROLE?.includes(roleName);

  return (
    <Box ref={sectionRef} className={classes.section}>
      <SectionHeader onAddClick={handleToggleAddForm} section={section} taskCount={taskCount} />
      {isSectionLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100px',
            p: 2,
          }}
        >
          <Loader size={40} />
        </Box>
      ) : (
        <Box ref={scrollContainerRef} className={classes?.taskStack} id={`scrollableDiv-${section?.id}`}>
          <Stack spacing={1}>
            {/* <InfiniteScroll
              dataLength={taskList?.length ?? 0}
              next={setNextPage}
              hasMore={hasMore}
              scrollableTarget={`scrollableDiv-${section?.id}`}
              className={classes?.infiniteScroll}
            > */}
            {taskList?.map((task) => {
              if (task?.projectId !== section?.id) return null;

              if (task?.taskName?.trim()?.toLowerCase() === 'not aware') return null;
              return (
                <TaskCard
                  key={task?.id}
                  task={task}
                  project={section}
                  onClick={handleTaskClick(task, section)} // section is project here
                  setLoading={setLoading}
                  runningTask={runningTask}
                  // refetchTask={refetchTask}
                  setTaskList={setTaskList}
                  setRunningTask={setRunningTask}
                  style={{ margin: '0px auto 10px auto' }}
                />
              );
            })}
            {showAddForm && (
              <AddTaskForm
                sectionId={userIsStakeholder && issuesSectionId ? issuesSectionId : firstSectionId} // Stakeholder -> Issues
                projectId={section?.id}
                onCancel={handleAddFormCancel}
                onSubmit={handleAddFormSubmit}
                defaultAssignToSelf
                projectDetails={
                  userIsAdmin || userIsStakeholder || isEditPermission || roleName !== 'Team Members'
                    ? projectDetails
                    : null
                }
                showAssigneeDropdown={userIsAdmin || isEditPermission || roleName !== 'Team Members'}
              />
            )}
            {/* </InfiniteScroll> */}
            {/* <Button color="inherit" className={classes?.addTaskBtn} onClick={handleAddTaskClick} startIcon={<Add />}>
              Add Task
            </Button> */}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

Section.propTypes = {
  section: PropTypes.object,
  setLoading: PropTypes.func,
  runningTask: PropTypes.object,
  setRunningTask: PropTypes.func,
  handleTaskClick: PropTypes.func,
  allTask: PropTypes.array,
  onRefetch: PropTypes.func,
  // ProjectAddTask Props
  // selected: PropTypes.object,
  // openModal: PropTypes.bool,
  // selectedProject: PropTypes.object,
  // handleClose: PropTypes.func,
  // action: PropTypes.string,
};

export default Section;
